<template>
    <div
        class="flex-1 flex flex-col md:flex-row items-start md:items-end gap-3 md:gap-8 md:pt-0"
    >
        <!-- Points Spent -->
        <dl
            class="w-full flex gap-2.5 sm:flex-row md:w-24 md:flex-col md:items-start md:justify-start md:gap-0"
        >
            <dt class="text-slate-500 text-sm sm:order-2 md:order-2">
                Points spent
            </dt>
            <dd
                class="text-slate-800 text-sm flex items-center gap-2 sm:order-1 md:order-1"
            >
                {{ Filters.formatThousand(redemption.payment_points) || 0 }}

                <ImageLoader v-if="redemption.payment_points">
                    <template #image>
                        <img
                            :src="`${cdnUrl.value}/${redemption?.payment_program?.program_image.path}/_small/${redemption?.payment_program?.program_image.file_name}`"
                            :alt="redemption?.payment_program?.name"
                            class="w-auto h-5 object-contain rounded-sm"
                        />
                    </template>
                    <template #error>
                        <Icon icon="credit-card" class="text-slate-500" />
                    </template>
                </ImageLoader>
            </dd>
        </dl>

        <!-- Cash Spent -->
        <dl
            class="w-full flex gap-2.5 sm:flex-row md:w-24 md:flex-col md:items-start md:justify-start md:gap-0"
        >
            <dt class="text-slate-500 text-sm sm:order-2 md:order-2">
                Cash spent
            </dt>
            <dd class="text-slate-800 text-sm sm:order-1 md:order-1">
                <button
                    class="flex items-center gap-2"
                    @click.stop="togglePopover()"
                    v-if="cashAmount"
                    ref="buttonRef"
                >
                    <span>
                        {{ Filters.formatMoney(cashAmount) || '$' + 0.0 }}
                    </span>
                    <Icon icon="chevron-down" :width="12" :height="12" />
                </button>
                <span v-else>$0.00</span>
                <!-- Popover -->
                <!-- <teleport to="body"> -->
                    <div
                        v-if="isPopoverOpen"
                        ref="popoverRef"
                        :style="floatingStyles"
                        class="z-30 bg-white border border-gray-200 rounded-lg shadow-lg p-4 flex-col items-start gap-3"
                        :class="{
                            'w-60': !(
                                isSouthwestFlight && wasCompanionPassUsed
                            ),
                            'w-96': isSouthwestFlight && wasCompanionPassUsed,
                        }"
                    >
                        <template
                            v-if="
                                parseFloat(redemption.advertised_price) +
                                    parseFloat(
                                        redemption.advertised_taxes_fees
                                    ) ==
                                0
                            "
                        >
                            <p class="text-slate-500 text-sm font-medium">
                                Details will display once you begin to add
                                redemptions to this trip.
                            </p>
                        </template>
                        <template v-else>
                            <template
                                v-if="redemption.payment_type !== 'points'"
                            >
                                <div class="flex justify-between mb-3">
                                    <h3
                                        class="text-slate-500 text-sm font-medium"
                                    >
                                        Cash cost
                                    </h3>
                                    <span class="text-sm text-slate-800">
                                        {{
                                            Filters.formatMoney(
                                                redemption.advertised_price
                                            )
                                        }}
                                    </span>
                                </div>
                                <div class="flex justify-between">
                                    <h3
                                        class="text-slate-500 text-sm font-medium"
                                    >
                                        Taxes & fees
                                    </h3>
                                    <span
                                        class="text-sm text-right text-slate-800"
                                    >
                                        {{
                                            Filters.formatMoney(
                                                redemption.advertised_taxes_fees
                                            )
                                        }}
                                    </span>
                                </div>
                                <!-- Companion Pass Info -->
                                <div
                                    v-if="
                                        isSouthwestFlight &&
                                        wasCompanionPassUsed
                                    "
                                    class="flex justify-between"
                                >
                                    <div></div>
                                    <h3
                                        class="text-slate-800 text-sm text-right font-normal leading-5"
                                    >
                                        (incl.
                                        {{
                                            Filters.formatMoney(
                                                companionTaxAmount
                                            )
                                        }}
                                        tax for companion)
                                    </h3>
                                </div>
                                <div
                                    class="mt-3 w-full h-0.5 bg-[#E2E8F0]"
                                ></div>

                                <!-- Total -->
                                <div class="mt-3 flex justify-between">
                                    <h3
                                        class="text-slate-500 text-sm font-medium"
                                    >
                                        Total
                                    </h3>
                                    <span
                                        class="text-sm text-right text-slate-800"
                                    >
                                        {{
                                            Filters.formatMoney(
                                                parseFloat(
                                                    redemption.advertised_price
                                                ) +
                                                    parseFloat(
                                                        redemption.advertised_taxes_fees
                                                    )
                                            )
                                        }}
                                    </span>
                                </div>
                                <!-- Companion Pass Row -->
                                <div
                                    v-if="
                                        isSouthwestFlight &&
                                        wasCompanionPassUsed
                                    "
                                    class="mt-1 flex justify-end items-center gap-2"
                                >
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <g clip-path="url(#clip0_828_13710)">
                                            <path
                                                fill-rule="evenodd"
                                                clip-rule="evenodd"
                                                d="M7.99902 3.99902C8.36721 3.99902 8.66569 4.2975 8.66569 4.66569V7.33236H11.3324C11.7005 7.33236 11.999 7.63083 11.999 7.99902C11.999 8.36721 11.7005 8.66569 11.3324 8.66569H8.66569V11.3324C8.66569 11.7005 8.36721 11.999 7.99902 11.999C7.63083 11.999 7.33236 11.7005 7.33236 11.3324V8.66569H4.66569C4.2975 8.66569 3.99902 8.36721 3.99902 7.99902C3.99902 7.63083 4.2975 7.33236 4.66569 7.33236H7.33236V4.66569C7.33236 4.2975 7.63083 3.99902 7.99902 3.99902Z"
                                                fill="#1E293B"
                                            />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_828_13710">
                                                <rect
                                                    width="16"
                                                    height="16"
                                                    fill="white"
                                                />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                    <svg
                                        width="95"
                                        height="14"
                                        viewBox="0 0 95 14"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <g clip-path="url(#clip0_834_7341)">
                                            <path
                                                fill-rule="evenodd"
                                                clip-rule="evenodd"
                                                d="M25.3204 11.1178C24.9374 11.1899 24.4904 11.2531 24.0098 11.2531C22.6664 11.2514 22.3705 10.7856 22.3709 9.90914V4.74993H19.8431L19.8381 10.1641C19.836 12.5653 21.171 13.6658 23.8882 13.6688C25.2064 13.6696 26.4324 13.4244 27.8713 13.0541V4.74993H25.3204V11.1178ZM33.5262 11.3545C32.607 11.3537 32.4803 10.9176 32.4808 10.2184L32.4837 6.89024L34.5805 6.89236V4.74993H32.4837V2.20066H29.9517V4.74951L28.6744 4.74993V6.88684L29.9517 6.88811L29.9488 10.383C29.9475 11.9218 30.2577 13.6756 32.772 13.6777C33.541 13.6785 34.1693 13.4919 34.8562 13.2959L34.6045 11.1738C34.2324 11.2824 33.9067 11.3545 33.5262 11.3545ZM6.98876 6.58351C6.52033 6.40278 5.9976 6.23308 5.43699 6.07696C3.9614 5.65696 3.50728 5.42236 3.5077 4.76945C3.50812 3.99817 4.07588 3.64393 5.39911 3.64521C6.48624 3.64605 7.45215 3.92945 8.43447 4.30151L8.96561 1.8023C7.83346 1.37721 6.75812 0.998357 5.45383 0.997508C2.40837 0.994963 0.72866 2.73902 0.726556 4.83181C0.725293 6.38199 1.42016 7.43496 2.91932 8.04587C3.35955 8.2249 3.86713 8.40224 4.42942 8.56896C6.08262 9.0806 6.5641 9.3203 6.56368 9.96133C6.56326 10.7385 5.93321 11.1526 4.63775 11.1513C3.44709 11.1501 2.3524 10.82 1.20762 10.2893L0.677734 12.8105C1.92731 13.3078 3.17184 13.6764 4.6689 13.6777C7.76738 13.6807 9.3747 11.9319 9.37639 9.83278C9.37807 8.1163 8.41763 7.1346 6.98876 6.58351ZM40.1466 4.3083C39.3166 4.30745 38.5237 4.50175 37.961 4.6986V0.499023H35.4328V13.6561H37.953L37.9585 7.06078C38.4143 6.88472 38.9404 6.71078 39.5624 6.71121C40.5468 6.71205 40.9029 7.0926 40.9021 8.01957L40.8974 13.6561H43.4181L43.4227 7.74254C43.4248 5.41811 42.3835 4.30999 40.1466 4.3083ZM14.554 4.2663C11.9976 4.26417 9.93405 6.07357 9.93152 8.93296C9.929 11.7932 11.9824 13.6756 14.5456 13.6777C17.107 13.6802 19.1638 11.8081 19.1664 8.94187C19.1689 6.07569 17.1083 4.26884 14.554 4.2663ZM14.5477 11.1747C13.4488 11.1738 12.5687 10.3779 12.57 8.93678C12.5713 7.49563 13.4239 6.73581 14.5515 6.73666C15.6773 6.73751 16.5119 7.48799 16.5106 8.93933C16.5098 10.3928 15.6457 11.1764 14.5477 11.1747ZM71.2451 8.23339C70.8852 8.10739 70.4808 7.97714 70.0401 7.84902C69.1546 7.61399 68.8111 7.46423 68.8116 7.09642C68.812 6.70823 69.1546 6.44987 69.9332 6.4503C70.7691 6.45114 71.5393 6.62466 72.3646 6.86563L72.7665 4.66466C71.8566 4.46399 70.9555 4.28751 69.9345 4.28624C67.5405 4.28411 66.2518 5.51908 66.2501 7.20333C66.2493 8.39969 66.8255 9.15569 68.0191 9.66436C68.3562 9.80902 68.7526 9.95539 69.2026 10.0975C70.1356 10.369 70.5902 10.4793 70.5902 10.904C70.5893 11.3083 70.0372 11.492 69.2964 11.4911C68.3848 11.4903 67.5338 11.3405 66.6554 10.9914L66.2623 13.1779C67.3499 13.4902 68.3701 13.6955 69.5018 13.6964C72.0191 13.6989 73.1483 12.3693 73.15 10.7288C73.1512 9.60581 72.671 8.73823 71.2451 8.23339ZM61.7501 4.27648C59.0784 4.27393 57.2249 6.15078 57.2223 9.10733C57.2202 11.8658 58.873 13.6853 61.7943 13.6879C63.0999 13.6887 64.1643 13.3201 65.259 12.8504L64.8793 10.7432C64.0544 11.1174 63.2013 11.4088 62.2051 11.408C60.6992 11.4063 60.0788 10.7542 59.9816 9.83278L65.6563 9.83787C65.6563 9.83787 65.7383 8.80017 65.7383 8.59824C65.7417 6.19745 64.4214 4.2786 61.7501 4.27648ZM60.0178 8.13199C60.179 7.35054 60.5826 6.38242 61.6445 6.38327C62.7388 6.38454 63.0683 7.50327 63.0965 8.13454L60.0178 8.13199ZM78.1361 11.3545C77.2169 11.3537 77.0952 10.9176 77.0961 10.2184L77.0994 6.9106L79.2202 6.91272V4.74993H77.0986V2.20066H74.562V4.74951L73.2728 4.74993V6.9072L74.562 6.90848L74.5586 10.383C74.5569 11.9218 74.9277 13.6756 77.442 13.6777C78.211 13.6785 78.8448 13.4919 79.5321 13.2959L79.2198 11.1738C78.8482 11.2824 78.517 11.3545 78.1361 11.3545ZM79.8196 1.02678C79.2047 1.02636 78.7042 1.53078 78.7034 2.15314C78.7026 2.77551 79.2034 3.28036 79.817 3.28121C80.4353 3.28163 80.9357 2.77721 80.9366 2.15484C80.9374 1.53248 80.437 1.02763 79.8196 1.02678ZM79.817 3.08181C79.2994 3.08139 78.913 2.70339 78.913 2.15357C78.9138 1.60672 79.301 1.22575 79.8187 1.2266C80.3406 1.22702 80.7244 1.60799 80.7236 2.15484C80.724 2.70466 80.3393 3.08181 79.817 3.08181ZM79.9559 2.20533C80.1483 2.17563 80.2775 2.06066 80.2775 1.86424C80.2775 1.64957 80.1281 1.48624 79.8431 1.48581L79.4021 1.48539L79.4012 2.79375L79.6411 2.79417V2.23502C79.7282 2.23545 79.7611 2.26217 79.8213 2.35933L80.0999 2.7946H80.3751L80.1087 2.38945C80.0489 2.29908 80.0047 2.24181 79.9559 2.20533ZM79.7678 2.06024L79.6411 2.05981L79.642 1.67927L79.8069 1.67969C79.9694 1.67969 80.0262 1.75563 80.0262 1.87017C80.0254 2.03011 79.9029 2.06024 79.7678 2.06024ZM54.8781 4.74993L53.2981 9.64908L51.6622 4.74993H49.4576L47.8221 9.64908L46.2417 4.74993H43.4947L46.6314 13.6552H48.9627L50.5729 8.83539L52.158 13.6552H54.4892L57.6255 4.74993H54.8781Z"
                                                fill="#304CB2"
                                            />
                                            <path
                                                d="M90.4508 2.75C89.4483 2.75 88.6409 3.09932 87.9957 3.46642C87.3499 3.09932 86.5441 2.75 85.5411 2.75C83.5366 2.75 82.2129 4.05933 82.2129 6.29655C82.2129 9.02588 84.8192 11.6925 87.9957 13.8333C91.1726 11.692 93.7785 9.02637 93.7785 6.29655C93.7785 4.05982 92.4558 2.75 90.4508 2.75Z"
                                                fill="#CCCCCC"
                                            />
                                            <path
                                                d="M82.541 6.34413C82.541 9.13472 85.4838 11.7307 87.9967 13.4491C88.72 12.954 89.4671 12.3858 90.1781 11.7613C86.849 8.97266 83.7244 6.47802 82.6033 5.58472C82.5633 5.82336 82.541 6.07781 82.541 6.34413Z"
                                                fill="url(#paint0_linear_834_7341)"
                                            />
                                            <path
                                                d="M93.2287 7.63939C90.816 5.6497 89.355 4.63683 87.7883 3.69658C87.3663 3.44163 86.519 3.06909 85.5689 3.06909C84.6587 3.06909 83.8944 3.3522 83.3677 3.9041C83.0248 4.26231 82.7921 4.71242 82.6592 5.25839C83.9087 6.13094 87.3016 8.5569 90.7192 11.2635C91.8605 10.177 92.8166 8.9413 93.2287 7.63939Z"
                                                fill="url(#paint1_linear_834_7341)"
                                            />
                                            <path
                                                d="M92.6175 3.90967C92.0987 3.36914 91.3383 3.07812 90.4633 3.07812C89.5245 3.07812 88.8091 3.38149 88.3105 3.65225C89.2463 4.17302 91.2795 5.35141 93.3858 6.90284C93.4184 6.69335 93.4367 6.48287 93.4367 6.2714C93.4362 5.28867 93.1491 4.46552 92.6175 3.90967Z"
                                                fill="url(#paint2_linear_834_7341)"
                                            />
                                        </g>
                                        <defs>
                                            <linearGradient
                                                id="paint0_linear_834_7341"
                                                x1="82.5412"
                                                y1="9.51665"
                                                x2="90.1783"
                                                y2="9.51665"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stop-color="#3656CC" />
                                                <stop
                                                    offset="0.35"
                                                    stop-color="#304CB2"
                                                />
                                                <stop
                                                    offset="0.72"
                                                    stop-color="#283B80"
                                                />
                                            </linearGradient>
                                            <linearGradient
                                                id="paint1_linear_834_7341"
                                                x1="83.6254"
                                                y1="3.48437"
                                                x2="92.2528"
                                                y2="9.52536"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop
                                                    offset="0.3"
                                                    stop-color="#E61C23"
                                                />
                                                <stop
                                                    offset="0.8"
                                                    stop-color="#C3161C"
                                                />
                                            </linearGradient>
                                            <linearGradient
                                                id="paint2_linear_834_7341"
                                                x1="88.9716"
                                                y1="2.70825"
                                                x2="93.9042"
                                                y2="6.16216"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stop-color="#FDBC11" />
                                                <stop
                                                    offset="0.25"
                                                    stop-color="#FCB415"
                                                />
                                                <stop
                                                    offset="0.8"
                                                    stop-color="#F89E1C"
                                                />
                                            </linearGradient>
                                            <clipPath id="clip0_834_7341">
                                                <rect
                                                    width="95"
                                                    height="14"
                                                    fill="white"
                                                />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                    <span
                                        class="text-slate-800 text-sm font-normal leading-5"
                                    >
                                        Southwest Companion Pass
                                    </span>
                                </div>
                            </template>
                            <template v-else>
                                <div class="flex justify-between">
                                    <h3
                                        class="text-slate-500 text-sm font-medium"
                                    >
                                        Taxes & fees
                                    </h3>
                                    <span
                                        class="text-sm text-right text-slate-800"
                                    >
                                        {{
                                            Filters.formatMoney(
                                                redemption.payment_taxes_fees
                                            )
                                        }}
                                    </span>
                                </div>
                                <!-- Companion Pass Info - Also for points redemptions -->
                                <div
                                    v-if="
                                        isSouthwestFlight &&
                                        wasCompanionPassUsed
                                    "
                                    class="flex justify-between mt-2"
                                >
                                    <div></div>
                                    <h3
                                        class="text-slate-800 text-sm text-right font-normal leading-5"
                                    >
                                        (incl.
                                        {{
                                            Filters.formatMoney(
                                                companionTaxAmount
                                            )
                                        }}
                                        tax for companion)
                                    </h3>
                                </div>
                                <div
                                    class="mt-2 w-full h-0.5 bg-[#E2E8F0]"
                                ></div>

                                <!-- Total Value -->
                                <div class="mt-4 flex justify-between">
                                    <h3
                                        class="text-slate-500 text-sm font-medium"
                                    >
                                        Total Value
                                    </h3>
                                    <span
                                        class="text-sm text-right text-slate-800"
                                    >
                                        {{
                                            Filters.formatMoney(
                                                parseFloat(
                                                    redemption.payment_taxes_fees
                                                )
                                            )
                                        }}
                                    </span>
                                </div>
                                <!-- Companion Pass Row for points redemptions -->
                                <div
                                    v-if="
                                        isSouthwestFlight &&
                                        wasCompanionPassUsed
                                    "
                                    class="mt-4 flex justify-end items-center gap-2"
                                >
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <g clip-path="url(#clip0_828_13710)">
                                            <path
                                                fill-rule="evenodd"
                                                clip-rule="evenodd"
                                                d="M7.99902 3.99902C8.36721 3.99902 8.66569 4.2975 8.66569 4.66569V7.33236H11.3324C11.7005 7.33236 11.999 7.63083 11.999 7.99902C11.999 8.36721 11.7005 8.66569 11.3324 8.66569H8.66569V11.3324C8.66569 11.7005 8.36721 11.999 7.99902 11.999C7.63083 11.999 7.33236 11.7005 7.33236 11.3324V8.66569H4.66569C4.2975 8.66569 3.99902 8.36721 3.99902 7.99902C3.99902 7.63083 4.2975 7.33236 4.66569 7.33236H7.33236V4.66569C7.33236 4.2975 7.63083 3.99902 7.99902 3.99902Z"
                                                fill="#1E293B"
                                            />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_828_13710">
                                                <rect
                                                    width="16"
                                                    height="16"
                                                    fill="white"
                                                />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                    <svg
                                        width="95"
                                        height="14"
                                        viewBox="0 0 95 14"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <g clip-path="url(#clip0_834_7341)">
                                            <path
                                                fill-rule="evenodd"
                                                clip-rule="evenodd"
                                                d="M25.3204 11.1178C24.9374 11.1899 24.4904 11.2531 24.0098 11.2531C22.6664 11.2514 22.3705 10.7856 22.3709 9.90914V4.74993H19.8431L19.8381 10.1641C19.836 12.5653 21.171 13.6658 23.8882 13.6688C25.2064 13.6696 26.4324 13.4244 27.8713 13.0541V4.74993H25.3204V11.1178ZM33.5262 11.3545C32.607 11.3537 32.4803 10.9176 32.4808 10.2184L32.4837 6.89024L34.5805 6.89236V4.74993H32.4837V2.20066H29.9517V4.74951L28.6744 4.74993V6.88684L29.9517 6.88811L29.9488 10.383C29.9475 11.9218 30.2577 13.6756 32.772 13.6777C33.541 13.6785 34.1693 13.4919 34.8562 13.2959L34.6045 11.1738C34.2324 11.2824 33.9067 11.3545 33.5262 11.3545ZM6.98876 6.58351C6.52033 6.40278 5.9976 6.23308 5.43699 6.07696C3.9614 5.65696 3.50728 5.42236 3.5077 4.76945C3.50812 3.99817 4.07588 3.64393 5.39911 3.64521C6.48624 3.64605 7.45215 3.92945 8.43447 4.30151L8.96561 1.8023C7.83346 1.37721 6.75812 0.998357 5.45383 0.997508C2.40837 0.994963 0.72866 2.73902 0.726556 4.83181C0.725293 6.38199 1.42016 7.43496 2.91932 8.04587C3.35955 8.2249 3.86713 8.40224 4.42942 8.56896C6.08262 9.0806 6.5641 9.3203 6.56368 9.96133C6.56326 10.7385 5.93321 11.1526 4.63775 11.1513C3.44709 11.1501 2.3524 10.82 1.20762 10.2893L0.677734 12.8105C1.92731 13.3078 3.17184 13.6764 4.6689 13.6777C7.76738 13.6807 9.3747 11.9319 9.37639 9.83278C9.37807 8.1163 8.41763 7.1346 6.98876 6.58351ZM40.1466 4.3083C39.3166 4.30745 38.5237 4.50175 37.961 4.6986V0.499023H35.4328V13.6561H37.953L37.9585 7.06078C38.4143 6.88472 38.9404 6.71078 39.5624 6.71121C40.5468 6.71205 40.9029 7.0926 40.9021 8.01957L40.8974 13.6561H43.4181L43.4227 7.74254C43.4248 5.41811 42.3835 4.30999 40.1466 4.3083ZM14.554 4.2663C11.9976 4.26417 9.93405 6.07357 9.93152 8.93296C9.929 11.7932 11.9824 13.6756 14.5456 13.6777C17.107 13.6802 19.1638 11.8081 19.1664 8.94187C19.1689 6.07569 17.1083 4.26884 14.554 4.2663ZM14.5477 11.1747C13.4488 11.1738 12.5687 10.3779 12.57 8.93678C12.5713 7.49563 13.4239 6.73581 14.5515 6.73666C15.6773 6.73751 16.5119 7.48799 16.5106 8.93933C16.5098 10.3928 15.6457 11.1764 14.5477 11.1747ZM71.2451 8.23339C70.8852 8.10739 70.4808 7.97714 70.0401 7.84902C69.1546 7.61399 68.8111 7.46423 68.8116 7.09642C68.812 6.70823 69.1546 6.44987 69.9332 6.4503C70.7691 6.45114 71.5393 6.62466 72.3646 6.86563L72.7665 4.66466C71.8566 4.46399 70.9555 4.28751 69.9345 4.28624C67.5405 4.28411 66.2518 5.51908 66.2501 7.20333C66.2493 8.39969 66.8255 9.15569 68.0191 9.66436C68.3562 9.80902 68.7526 9.95539 69.2026 10.0975C70.1356 10.369 70.5902 10.4793 70.5902 10.904C70.5893 11.3083 70.0372 11.492 69.2964 11.4911C68.3848 11.4903 67.5338 11.3405 66.6554 10.9914L66.2623 13.1779C67.3499 13.4902 68.3701 13.6955 69.5018 13.6964C72.0191 13.6989 73.1483 12.3693 73.15 10.7288C73.1512 9.60581 72.671 8.73823 71.2451 8.23339ZM61.7501 4.27648C59.0784 4.27393 57.2249 6.15078 57.2223 9.10733C57.2202 11.8658 58.873 13.6853 61.7943 13.6879C63.0999 13.6887 64.1643 13.3201 65.259 12.8504L64.8793 10.7432C64.0544 11.1174 63.2013 11.4088 62.2051 11.408C60.6992 11.4063 60.0788 10.7542 59.9816 9.83278L65.6563 9.83787C65.6563 9.83787 65.7383 8.80017 65.7383 8.59824C65.7417 6.19745 64.4214 4.2786 61.7501 4.27648ZM60.0178 8.13199C60.179 7.35054 60.5826 6.38242 61.6445 6.38327C62.7388 6.38454 63.0683 7.50327 63.0965 8.13454L60.0178 8.13199ZM78.1361 11.3545C77.2169 11.3537 77.0952 10.9176 77.0961 10.2184L77.0994 6.9106L79.2202 6.91272V4.74993H77.0986V2.20066H74.562V4.74951L73.2728 4.74993V6.9072L74.562 6.90848L74.5586 10.383C74.5569 11.9218 74.9277 13.6756 77.442 13.6777C78.211 13.6785 78.8448 13.4919 79.5321 13.2959L79.2198 11.1738C78.8482 11.2824 78.517 11.3545 78.1361 11.3545ZM79.8196 1.02678C79.2047 1.02636 78.7042 1.53078 78.7034 2.15314C78.7026 2.77551 79.2034 3.28036 79.817 3.28121C80.4353 3.28163 80.9357 2.77721 80.9366 2.15484C80.9374 1.53248 80.437 1.02763 79.8196 1.02678ZM79.817 3.08181C79.2994 3.08139 78.913 2.70339 78.913 2.15357C78.9138 1.60672 79.301 1.22575 79.8187 1.2266C80.3406 1.22702 80.7244 1.60799 80.7236 2.15484C80.724 2.70466 80.3393 3.08181 79.817 3.08181ZM79.9559 2.20533C80.1483 2.17563 80.2775 2.06066 80.2775 1.86424C80.2775 1.64957 80.1281 1.48624 79.8431 1.48581L79.4021 1.48539L79.4012 2.79375L79.6411 2.79417V2.23502C79.7282 2.23545 79.7611 2.26217 79.8213 2.35933L80.0999 2.7946H80.3751L80.1087 2.38945C80.0489 2.29908 80.0047 2.24181 79.9559 2.20533ZM79.7678 2.06024L79.6411 2.05981L79.642 1.67927L79.8069 1.67969C79.9694 1.67969 80.0262 1.75563 80.0262 1.87017C80.0254 2.03011 79.9029 2.06024 79.7678 2.06024ZM54.8781 4.74993L53.2981 9.64908L51.6622 4.74993H49.4576L47.8221 9.64908L46.2417 4.74993H43.4947L46.6314 13.6552H48.9627L50.5729 8.83539L52.158 13.6552H54.4892L57.6255 4.74993H54.8781Z"
                                                fill="#304CB2"
                                            />
                                            <path
                                                d="M90.4508 2.75C89.4483 2.75 88.6409 3.09932 87.9957 3.46642C87.3499 3.09932 86.5441 2.75 85.5411 2.75C83.5366 2.75 82.2129 4.05933 82.2129 6.29655C82.2129 9.02588 84.8192 11.6925 87.9957 13.8333C91.1726 11.692 93.7785 9.02637 93.7785 6.29655C93.7785 4.05982 92.4558 2.75 90.4508 2.75Z"
                                                fill="#CCCCCC"
                                            />
                                            <path
                                                d="M82.541 6.34413C82.541 9.13472 85.4838 11.7307 87.9967 13.4491C88.72 12.954 89.4671 12.3858 90.1781 11.7613C86.849 8.97266 83.7244 6.47802 82.6033 5.58472C82.5633 5.82336 82.541 6.07781 82.541 6.34413Z"
                                                fill="url(#paint0_linear_834_7341)"
                                            />
                                            <path
                                                d="M93.2287 7.63939C90.816 5.6497 89.355 4.63683 87.7883 3.69658C87.3663 3.44163 86.519 3.06909 85.5689 3.06909C84.6587 3.06909 83.8944 3.3522 83.3677 3.9041C83.0248 4.26231 82.7921 4.71242 82.6592 5.25839C83.9087 6.13094 87.3016 8.5569 90.7192 11.2635C91.8605 10.177 92.8166 8.9413 93.2287 7.63939Z"
                                                fill="url(#paint1_linear_834_7341)"
                                            />
                                            <path
                                                d="M92.6175 3.90967C92.0987 3.36914 91.3383 3.07812 90.4633 3.07812C89.5245 3.07812 88.8091 3.38149 88.3105 3.65225C89.2463 4.17302 91.2795 5.35141 93.3858 6.90284C93.4184 6.69335 93.4367 6.48287 93.4367 6.2714C93.4362 5.28867 93.1491 4.46552 92.6175 3.90967Z"
                                                fill="url(#paint2_linear_834_7341)"
                                            />
                                        </g>
                                        <defs>
                                            <linearGradient
                                                id="paint0_linear_834_7341"
                                                x1="82.5412"
                                                y1="9.51665"
                                                x2="90.1783"
                                                y2="9.51665"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stop-color="#3656CC" />
                                                <stop
                                                    offset="0.35"
                                                    stop-color="#304CB2"
                                                />
                                                <stop
                                                    offset="0.72"
                                                    stop-color="#283B80"
                                                />
                                            </linearGradient>
                                            <linearGradient
                                                id="paint1_linear_834_7341"
                                                x1="83.6254"
                                                y1="3.48437"
                                                x2="92.2528"
                                                y2="9.52536"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop
                                                    offset="0.3"
                                                    stop-color="#E61C23"
                                                />
                                                <stop
                                                    offset="0.8"
                                                    stop-color="#C3161C"
                                                />
                                            </linearGradient>
                                            <linearGradient
                                                id="paint2_linear_834_7341"
                                                x1="88.9716"
                                                y1="2.70825"
                                                x2="93.9042"
                                                y2="6.16216"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stop-color="#FDBC11" />
                                                <stop
                                                    offset="0.25"
                                                    stop-color="#FCB415"
                                                />
                                                <stop
                                                    offset="0.8"
                                                    stop-color="#F89E1C"
                                                />
                                            </linearGradient>
                                            <clipPath id="clip0_834_7341">
                                                <rect
                                                    width="95"
                                                    height="14"
                                                    fill="white"
                                                />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                    <span
                                        class="text-slate-800 text-sm font-normal leading-5"
                                    >
                                        Southwest Companion Pass
                                    </span>
                                </div>
                            </template>
                        </template>
                    </div>
                <!-- </teleport> -->
            </dd>
        </dl>

        <!-- CPP -->
        <dl
            class="w-full flex gap-2.5 sm:flex-row md:w-24 md:flex-col md:items-start md:justify-start md:gap-0"
        >
            <dt class="text-slate-500 text-sm sm:order-2 md:order-2">CPP</dt>
            <dd class="text-slate-800 text-sm sm:order-1 md:order-1">
                <Cpp :redemption="redemption" />
            </dd>
        </dl>
    </div>
</template>
<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import {
    computePosition,
    flip,
    shift,
    offset,
    useFloating,
} from '@floating-ui/vue';

import Cpp from '@/components/trips/redemptions/cpp.vue';
import Icon from '@/components/ui/icon/icon.vue';
import Filters from '@/filters';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';

const props = defineProps({
    redemption: {
        type: Object,
        required: true,
    },
});
const popoverRef = ref(null);
const buttonRef = ref(null);
const isPopoverOpen = ref(false);

const togglePopover = () => {
    isPopoverOpen.value = !isPopoverOpen.value;
};

const handleClickOutside = (event) => {
    if (popoverRef.value && !popoverRef.value.contains(event.target)) {
        closePopover();
    }
};

const closePopover = () => {
    isPopoverOpen.value = false;
};

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
});

const cashAmount = computed(() => {
    if (props.redemption.payment_type === 'cash') {
        return (
            parseFloat(props.redemption.advertised_price) +
            parseFloat(props.redemption.advertised_taxes_fees ?? 0)
        );
    }

    if (props.redemption.payment_type === 'cash_points') {
        return (
            parseFloat(props.redemption.payment_cash_amount) +
            parseFloat(props.redemption.payment_taxes_fees ?? 0)
        );
    }

    if (props.redemption.payment_type === 'points') {
        return parseFloat(props.redemption.payment_taxes_fees ?? 0);
    }

    return 0.0;
});

const isSouthwestFlight = computed(() => {
    if (
        !props.redemption.travel_segments ||
        !props.redemption.travel_segments.length
    ) {
        return false;
    }

    const isSW = props.redemption.travel_segments.some(
        (segment) =>
            segment.travel_number &&
            segment.travel_number.toLowerCase().startsWith('wn')
    );
    return isSW;
});

const wasCompanionPassUsed = computed(() => {
    if (
        props.redemption.payment_type !== 'cash' &&
        props.redemption.payment_type !== 'points'
    ) {
        return false;
    }

    if (!isSouthwestFlight.value) {
        return false;
    }

    return props.redemption.companion_pass_used === true;
});

const companionTaxAmount = computed(() => {
    if (props.redemption.companion_tax_amount) {
        return parseFloat(props.redemption.companion_tax_amount);
    }

    const taxesAndFees = parseFloat(
        props.redemption.advertised_taxes_fees || 0
    );
    return taxesAndFees > 0 ? taxesAndFees / 2 : 0;
});

const cdnUrl = computed(() => {
    return window.cdnUrl;
});

const { floatingStyles } = useFloating(buttonRef, popoverRef, {
    placement: 'top-start',
    middleware: [offset(4), flip(), shift({ padding: 8 })],
});
</script>
